import { isAfter, isBefore } from '~/services/TimeService';

import { CartRawItem } from '~/types/services/carts';

// Narrow the CartRawItem union to the variant for a specific itemType.
type ItemOfType<T extends string> = Extract<CartRawItem, { itemType: T }>;

export interface CartItemInfo {
  travellerCount: number;
  travelStart: string | null;
  travelEnd: string | null;
}

type ItemHandler<T extends CartRawItem> = (item: T) => CartItemInfo;

const EMPTY_INFO: CartItemInfo = {
  travellerCount: 0,
  travelStart: null,
  travelEnd: null,
};

interface CartItemOccupancy {
  adults?: number;
  teenagers?: number;
  children?: number;
  infants?: number;
}

function sumOccupancy(occupancy: CartItemOccupancy | undefined): number {
  if (!occupancy) {
    return EMPTY_INFO.travellerCount;
  }

  return (occupancy.adults ?? 0) + (occupancy.teenagers ?? 0) + (occupancy.children ?? 0) + (occupancy.infants ?? 0);
}

// -----------------------------------------------------------------------------
// Item type handlers
// -----------------------------------------------------------------------------

// Hotel instant_booking has occupancy and checkIn/checkOut; buy_now_book_later has neither.
const hotelHandler: ItemHandler<ItemOfType<'hotel'>> = (item) => {
  if (item.reservationType === 'instant_booking') {
    return {
      travellerCount: sumOccupancy(item.occupancy),
      travelStart: item.checkIn,
      travelEnd: item.checkOut,
    };
  }
  return EMPTY_INFO;
};

// Bedbank hotel has occupancy and checkIn/checkOut.
const bedbankHotelHandler: ItemHandler<ItemOfType<'bedbankHotel'>> = (item) => ({
  travellerCount: sumOccupancy(item.occupancy),
  travelStart: item.checkIn ?? null,
  travelEnd: item.checkOut ?? null,
});

// Villa has occupancy and checkIn/checkOut.
const villaHandler: ItemHandler<ItemOfType<'villa'>> = (item) => ({
  travellerCount: sumOccupancy(item.occupancy),
  travelStart: item.checkIn ?? null,
  travelEnd: item.checkOut ?? null,
});

// bundleAndSave has occupancy and nested `dates: Record<string, { checkIn, checkOut }>`.
// Take the earliest checkIn and the latest checkOut across every leg in the map.
const bundleAndSaveHandler: ItemHandler<ItemOfType<'bundleAndSave'>> = (item) => {
  const entries = Object.values(item.dates ?? {});
  const starts = entries.map((d) => d.checkIn).filter((v): v is string => !!v);
  const ends = entries.map((d) => d.checkOut).filter((v): v is string => !!v);
  return {
    travellerCount: sumOccupancy(item.occupancy),
    travelStart: starts.length ? starts.reduce((min, curr) => (isBefore(curr, min) ? curr : min)) : null,
    travelEnd: ends.length ? ends.reduce((max, curr) => (isAfter(curr, max) ? curr : max)) : null,
  };
};

// tourV1 has occupancy and optional startDate/endDate.
const tourV1Handler: ItemHandler<ItemOfType<'tourV1'>> = (item) => ({
  travellerCount: sumOccupancy(item.occupancy),
  travelStart: item.startDate ?? null,
  travelEnd: item.endDate ?? null,
});

// tourV2 has occupancy and startDate/endDate.
const tourV2Handler: ItemHandler<ItemOfType<'tourV2'>> = (item) => ({
  travellerCount: sumOccupancy(item.occupancy),
  travelStart: item.startDate ?? null,
  travelEnd: item.endDate ?? null,
});

// tourV2Experience has `occupants` as an ARRAY of occupancy objects, and a single-day `date`.
const tourV2ExperienceHandler: ItemHandler<ItemOfType<'tourV2Experience'>> = (item) => {
  const total = (item.occupants ?? []).reduce((sum, occ) => sum + sumOccupancy(occ), 0);
  return {
    travellerCount: total,
    travelStart: item.date ?? null,
    travelEnd: item.date ?? null,
  };
};

// Flight has no occupancy — count comes from `passengers[]`; dates via travelStart/travelEnd.
const flightHandler: ItemHandler<ItemOfType<'flight'>> = (item) => ({
  travellerCount: item.passengers?.length ?? EMPTY_INFO.travellerCount,
  travelStart: item.travelStart ?? null,
  travelEnd: item.travelEnd ?? null,
});

// Cruise has occupancy and startDate/endDate.
const cruiseHandler: ItemHandler<ItemOfType<'cruise'>> = (item) => ({
  travellerCount: sumOccupancy(item.occupancy),
  travelStart: item.startDate ?? null,
  travelEnd: item.endDate ?? null,
});

// Car hire has no occupancy; dates via pickUpDate/dropOffDate.
const carHireHandler: ItemHandler<ItemOfType<'car-hire'>> = (item) => ({
  travellerCount: EMPTY_INFO.travellerCount,
  travelStart: item.pickUpDate ?? null,
  travelEnd: item.dropOffDate ?? null,
});

// Experience has no occupancy and a single-day `date` (optional).
const experienceHandler: ItemHandler<ItemOfType<'experience'>> = (item) => ({
  travellerCount: EMPTY_INFO.travellerCount,
  travelStart: item.date ?? null,
  travelEnd: item.date ?? null,
});

// Transfer has both nested under `transfer`: `transfer.travellers` and single-day `transfer.date`.
const transferHandler: ItemHandler<ItemOfType<'transfer'>> = (item) => {
  const t = item.transfer.travellers;
  return {
    travellerCount: (t.adults ?? 0) + (t.children ?? 0),
    travelStart: item.transfer.date ?? null,
    travelEnd: item.transfer.date ?? null,
  };
};

// Insurance has no occupancy; dates via optional startDate/endDate (policy period).
const insuranceHandler: ItemHandler<ItemOfType<'insurance'>> = (item) => ({
  travellerCount: EMPTY_INFO.travellerCount,
  travelStart: item.startDate ?? null,
  travelEnd: item.endDate ?? null,
});

// Lounge pass has no occupancy and a single-day `date` (optional).
const loungePassHandler: ItemHandler<ItemOfType<'loungePassItem'>> = (item) => ({
  travellerCount: EMPTY_INFO.travellerCount,
  travelStart: item.date ?? null,
  travelEnd: item.date ?? null,
});

// booking-protection / subscription-join / luxury-plus-subscription / merchandise /
// loyaltyTransferItem — none have occupancy or dates.
const noTravelInfoHandler: ItemHandler<CartRawItem> = () => EMPTY_INFO;

// -----------------------------------------------------------------------------
// Dispatcher
// -----------------------------------------------------------------------------

export function getCartItemInfo(item: CartRawItem): CartItemInfo {
  /* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
  switch (item.itemType) {
    case 'hotel':
      return hotelHandler(item as ItemOfType<'hotel'>);
    case 'bedbankHotel':
      return bedbankHotelHandler(item as ItemOfType<'bedbankHotel'>);
    case 'villa':
      return villaHandler(item as ItemOfType<'villa'>);
    case 'bundleAndSave':
      return bundleAndSaveHandler(item as ItemOfType<'bundleAndSave'>);
    case 'tourV1':
      return tourV1Handler(item as ItemOfType<'tourV1'>);
    case 'tourV2':
      return tourV2Handler(item as ItemOfType<'tourV2'>);
    case 'tourV2Experience':
      return tourV2ExperienceHandler(item as ItemOfType<'tourV2Experience'>);
    case 'flight':
      return flightHandler(item as ItemOfType<'flight'>);
    case 'cruise':
      return cruiseHandler(item as ItemOfType<'cruise'>);
    case 'car-hire':
      return carHireHandler(item as ItemOfType<'car-hire'>);
    case 'experience':
      return experienceHandler(item as ItemOfType<'experience'>);
    case 'transfer':
      return transferHandler(item as ItemOfType<'transfer'>);
    case 'insurance':
      return insuranceHandler(item as ItemOfType<'insurance'>);
    case 'loungePassItem':
      return loungePassHandler(item as ItemOfType<'loungePassItem'>);
    case 'booking-protection':
    case 'subscription-join':
    case 'luxury-plus-subscription':
    case 'merchandise':
    case 'loyaltyTransferItem':
      return noTravelInfoHandler(item);
    default:
      // Unknown item types — return empty info
      return EMPTY_INFO;
  }
}
